import { useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { DndContext } from '@dnd-kit/core'
import { ArrowLeft, EyeOff, Lock } from 'lucide-react'
import { ApiError } from '@/shared/api/client'
import { useUserTeam } from '@/features/squad/queries'
import { useLeagueGameweekStatus } from '@/features/league/queries'
import { TacticalPitch } from '@/features/squad/components/TacticalPitch'
import { BenchSection } from '@/features/squad/components/BenchSection'
import { PlayerStatsSheet } from '@/features/squad/components/PlayerStatsSheet'
import { GameweekNavigator } from '@/features/league/components/GameweekNavigator'
import { usePlayerStatsSheet } from '@/features/squad/hooks/usePlayerStatsSheet'
import { squadToDraft } from '@/features/squad/squadUtils'
import type { SquadDraft } from '@/features/squad/types'
import styles from './TeamViewer.module.css'
import { withLegacySkin } from '@/shared/components/layout/LegacySkin'

export const Route = createFileRoute('/_authenticated/league/$leagueId/team/$userId')({
  validateSearch: (search: Record<string, unknown>) => ({
    gw: typeof search.gw === 'string' ? search.gw : undefined,
  }),
  component: withLegacySkin(TeamViewerPage),
})

const EMPTY_DRAFT: SquadDraft = { starters: [], benchGk: null, bench: [], isDirty: false }

function TeamViewerPage() {
  const { leagueId, userId } = Route.useParams()
  const { gw }               = Route.useSearch()
  const navigate             = useNavigate()

  const { data: gwStatus } = useLeagueGameweekStatus(leagueId)

  // Map ?gw=<number> to the gameweekId via the league's gameweek list.
  const gameweekId = useMemo(() => {
    if (!gw || !gwStatus) return undefined
    const n = parseInt(gw, 10)
    if (!Number.isFinite(n)) return undefined
    return gwStatus.gameweeks.find(g => g.number === n)?.id
  }, [gw, gwStatus])

  // Wait for the GW lookup before fetching when a specific gw was requested.
  const shouldFetch = !gw || gameweekId !== undefined
  const teamQuery = useUserTeam(leagueId, userId, gameweekId)
  const isLoading = teamQuery.isPending && shouldFetch

  // GW navigator state
  const currentGwNumber  = gwStatus?.currentGameweekNumber ?? 1
  const totalGws         = gwStatus?.gameweeks.length ?? 8
  const selectedGwNumber = (() => {
    if (gw) {
      const n = parseInt(gw, 10)
      if (Number.isFinite(n)) return n
    }
    return teamQuery.data?.gameweekNumber ?? currentGwNumber
  })()
  const isWindowOpen = (gwStatus?.transferWindowState ?? 'closed') === 'open'

  // Player click → read-only stats sheet (same sheet as the squad picker).
  // Computed before the early returns so the hook order stays stable.
  const draft = useMemo(
    () => (teamQuery.data ? squadToDraft(teamQuery.data) : EMPTY_DRAFT),
    [teamQuery.data],
  )
  const { selectPlayer, clearSelection, playerForSheet } = usePlayerStatsSheet(draft)

  function goBack() {
    void navigate({ to: '/league/$leagueId', params: { leagueId }, search: { tab: 'tabela', gw: undefined } })
  }

  function handleGwPrev() {
    const target = selectedGwNumber - 1
    if (target < 1) return
    void navigate({
      to:     '/league/$leagueId/team/$userId',
      params: { leagueId, userId },
      search: { gw: String(target) },
      replace: true,
    })
  }

  function handleGwNext() {
    const target = selectedGwNumber + 1
    if (target > currentGwNumber) return
    void navigate({
      to:     '/league/$leagueId/team/$userId',
      params: { leagueId, userId },
      search: { gw: String(target) },
      replace: true,
    })
  }

  const navigator = (
    <GameweekNavigator
      selectedGwNumber={selectedGwNumber}
      currentGwNumber={currentGwNumber}
      totalGws={totalGws}
      onPrev={handleGwPrev}
      onNext={handleGwNext}
      isLoading={teamQuery.isFetching}
    />
  )

  // ── Error states ───────────────────────────────────────────────────────────
  if (teamQuery.error instanceof ApiError) {
    const err = teamQuery.error
    return (
      <div className={styles.page}>
        <Header title="Equipe" onBack={goBack} />
        <div className={styles.content}>
          {navigator}
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>
              {err.errorCode === 'squad_not_visible' ? <EyeOff size={32} /> : <Lock size={32} />}
            </div>
            <h2 className={styles.emptyHeading}>
              {err.errorCode === 'squad_not_visible'
                ? 'Escalação ainda não disponível'
                : 'Equipe indisponível'}
            </h2>
            <p className={styles.emptyDescription}>
              {err.errorCode === 'squad_not_visible'
                ? 'A escalação atual só fica visível para outros usuários após o fechamento da janela de transferências.'
                : err.detail || 'Não foi possível carregar esta equipe.'}
            </p>
            <button className={styles.emptyBtn} onClick={goBack}>
              Voltar para a liga
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading || !teamQuery.data) {
    return (
      <div className={styles.page}>
        <Header title="Carregando..." onBack={goBack} />
        <div className={styles.content}>{navigator}</div>
      </div>
    )
  }

  const team = teamQuery.data
  const subbedOutIds = new Set(team.substitutions?.map(s => s.playerOutId) ?? [])
  const subbedInIds  = new Set(team.substitutions?.map(s => s.playerInId)  ?? [])

  // Current-GW points: historical = NetPoints from BE; live = 0 when window is open
  // (no matches yet), else sum of starter gwPoints.
  const gwPointsDisplay = team.isHistoricalSnapshot
    ? team.gameweekNetPoints ?? 0
    : isWindowOpen
      ? 0
      : draft.starters.reduce((sum, p) => sum + (p.gwPoints ?? 0), 0)

  return (
    <div className={styles.page}>
      <Header title={team.teamName} onBack={goBack} />

      <div className={styles.content}>
        {navigator}

        <div className={styles.statsBar}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Pts Rodada</span>
            <span className={`${styles.statValue} ${styles.statValueAccent}`}>
              {gwPointsDisplay}
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Orçamento</span>
            <span className={styles.statValue}>${team.budgetUsed.toFixed(1)}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Transferências</span>
            <span className={styles.statValue}>{team.totalTransfersUsed}</span>
          </div>
        </div>

        {/* DndContext is required by the slot components (hooks are called but disabled). */}
        <DndContext>
          <TacticalPitch
            starters={draft.starters}
            isWindowOpen={false}
            subbedOutIds={subbedOutIds}
            onClickPlayer={selectPlayer}
          />
          <BenchSection
            benchGk={draft.benchGk}
            bench={draft.bench}
            isWindowOpen={false}
            subbedInIds={subbedInIds}
            onClickPlayer={selectPlayer}
          />
        </DndContext>
      </div>

      {/* Read-only player stats — same sheet as the squad picker, no add/remove */}
      {playerForSheet && (
        <PlayerStatsSheet
          readOnly
          player={playerForSheet}
          onClose={clearSelection}
        />
      )}
    </div>
  )
}

function Header({
  title,
  onBack,
}: {
  title: string
  onBack: () => void
}) {
  return (
    <header className={styles.header}>
      <button className={styles.backBtn} onClick={onBack} aria-label="Voltar para a liga">
        <ArrowLeft size={20} />
      </button>
      <div className={styles.titleBlock}>
        <span className={styles.teamName}>{title}</span>
      </div>
    </header>
  )
}
